import { evictCacheByKeys } from 'helpers';
import { MutationHookOptions, useMutation } from '@apollo/client';
import { UpdatePackFavoritedState } from 'apollo/pack/pack.gql';
import { UpdateRepackFavoritedState } from 'apollo/repack/repack.gql';
import { UpdateSampleFavoritedState } from 'apollo/sample';
import { FavoriteItem } from 'types';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import FavoritesPageQueryConfig from 'apollo/favorites/favorites';
import {
    AddedToFavoritesError,
    AddedToFavoritesSuccess,
    RemovedFromFavoritesError,
    RemovedFromFavoritesSuccess,
} from 'contexts/NotificationsContext/notifications';
import { isPack } from 'types/typeGuards/isPack';
import { isRepack } from 'types/typeGuards/isRepack';
import { isSample } from 'types/typeGuards/isSample';

const getMutationConfig = (
    item: FavoriteItem,
    handleErrorNotifications: (item: FavoriteItem) => void,
    handleSuccessNotifications: (item: FavoriteItem) => void,
): MutationHookOptions => {
    const updatePayload = {
        guid: item.guid,
        // info: updatePayload.isFavorited is the item current value
        isFavorited: item.isFavorited,
    };

    switch (true) {
        case isPack(item):
            return {
                variables: {
                    pack: updatePayload,
                },
                update: (cache, { data: { updatePackFavoritedState } }) => {
                    if (updatePackFavoritedState.guid) {
                        handleSuccessNotifications(item);

                        cache.modify({
                            id: cache.identify(updatePackFavoritedState),
                            fields: {
                                isFavorited() {
                                    return !updatePayload.isFavorited;
                                },
                            },
                        });

                        evictCacheByKeys(cache, [
                            FavoritesPageQueryConfig.packs.pathToItems,
                        ]);
                    }
                },
                onQueryUpdated(observableQuery) {
                    return observableQuery.refetch();
                },
                onError: () => {
                    handleErrorNotifications(item);
                },
            };
        case isRepack(item):
            return {
                variables: {
                    repack: updatePayload,
                },
                update: (cache, { data: { updateRepackFavoritedState } }) => {
                    if (updateRepackFavoritedState.guid) {
                        handleSuccessNotifications(item);
                        cache.modify({
                            id: cache.identify(updateRepackFavoritedState),
                            fields: {
                                isFavorited() {
                                    return !updatePayload.isFavorited;
                                },
                            },
                        });
                        evictCacheByKeys(cache, [
                            FavoritesPageQueryConfig.packs.pathToItems,
                        ]);
                    }
                },
                onQueryUpdated(observableQuery) {
                    return observableQuery.refetch();
                },
                onError: () => {
                    handleErrorNotifications(item);
                },
            };
        case isSample(item):
            return {
                variables: {
                    sample: updatePayload,
                },
                update: (cache, { data: { updateSampleFavoritedState } }) => {
                    if (updateSampleFavoritedState.guid) {
                        handleSuccessNotifications(item);

                        cache.modify({
                            id: cache.identify(updateSampleFavoritedState),
                            fields: {
                                isFavorited() {
                                    return !updatePayload.isFavorited;
                                },
                            },
                        });

                        evictCacheByKeys(cache, [
                            FavoritesPageQueryConfig.samples.pathToItems,
                        ]);
                    }
                },
                onQueryUpdated(observableQuery) {
                    return observableQuery.refetch();
                },
                onError: () => {
                    handleErrorNotifications(item);
                },
            };
        default:
            // log error
            throw new Error('');
    }
};

export type UpdateFavoriteItemType = (
    item: FavoriteItem,
    handleNotAuthenticated?: () => void,
) => void;

export const useUpdateItemFavoriteStatus = (): {
    updateFavorite: UpdateFavoriteItemType;
} => {
    const [updatePack] = useMutation(UpdatePackFavoritedState);
    const [updateRepack] = useMutation(UpdateRepackFavoritedState);
    const [updateSample] = useMutation(UpdateSampleFavoritedState);

    const { createNotification } = useNotificationsContext();

    const handleErrorMessage = (item: FavoriteItem) => {
        if (item.isFavorited) {
            createNotification(new RemovedFromFavoritesError(item.name));
        } else {
            createNotification(new AddedToFavoritesError(item.name));
        }
    };

    const handleSuccessMessage = (item: FavoriteItem) => {
        if (item.isFavorited) {
            createNotification(new RemovedFromFavoritesSuccess(item.name));
        } else {
            createNotification(new AddedToFavoritesSuccess(item.name));
        }
    };

    const updateFavorite: UpdateFavoriteItemType = (item) => {
        const options = getMutationConfig(
            item,
            handleErrorMessage,
            handleSuccessMessage,
        );

        switch (true) {
            case isPack(item):
                updatePack(options);
                break;
            case isRepack(item):
                updateRepack(options);
                break;
            case isSample(item):
                updateSample(options);
                break;
            default:
                // log error
                break;
        }
    };

    return {
        updateFavorite,
    };
};
